import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WelcomeCard from '../components/dashboard/WelcomeCard';
import PendingTasksCard, { taskSources } from '../components/dashboard/PendingTasksCard';
import TimeOffCard from '../components/dashboard/TimeOffCard';
import WhoIsOffCard from '../components/dashboard/WhoIsOffCard';
import EventsCard from '../components/dashboard/EventsCard';
import OrgOverview, { orgGates, hasOrgOverview } from '../components/dashboard/OrgOverview';
import { canSeeTeamCalendar } from '../components/dashboard/teamAccess';

/**
 * One dashboard for every role, centred on the signed-in person: today's attendance,
 * what is waiting on them, their time off, who on their team is away and the month's
 * events. HR, admins and managers also get the organization overview below it. Each card
 * is gated on the permission its data needs and loads on its own.
 */
const Dashboard = () => {
  const auth = useAuth();
  const { user, hasAnyResourceAction } = auth;

  const sources = taskSources(auth);
  const showTimeOff = Boolean(user?.employee_id)
    && hasAnyResourceAction('leaves', ['read_own', 'read_department', 'read_all']);
  const showTeam = canSeeTeamCalendar(auth);
  const showOrg = hasOrgOverview(orgGates(auth));

  // The personal cards share a two-column grid; an odd one out takes the full row, so
  // no card sits beside an empty cell.
  const personal = [
    { key: 'welcome', render: (className) => <WelcomeCard className={className} /> },
    sources.length > 0 && {
      key: 'tasks', render: (className) => <PendingTasksCard sources={sources} className={className} />,
    },
    showTimeOff && { key: 'time-off', render: (className) => <TimeOffCard className={className} /> },
    showTeam && { key: 'who-is-off', render: (className) => <WhoIsOffCard className={className} /> },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to ByteCorp HR Portal</p>
      </div>

      {/* Onboarding banner for users without employee record */}
      {user?.employee_id === null && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-warning-800">
              Complete your employee onboarding
            </p>
            <p className="text-xs text-warning-600 mt-0.5">
              Fill in your personal details to get full access to the portal.
            </p>
          </div>
          <Link
            to="/onboarding"
            className="text-sm font-medium text-warning-700 hover:text-warning-900 underline sm:ml-4 shrink-0"
          >
            Start now →
          </Link>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-6 ${showTeam ? 'xl:grid-cols-3' : ''}`}>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 content-start ${showTeam ? 'xl:col-span-2' : ''}`}>
          {personal.map((card, index) => (
            <React.Fragment key={card.key}>
              {card.render(personal.length % 2 === 1 && index === personal.length - 1 ? 'md:col-span-2' : '')}
            </React.Fragment>
          ))}
        </div>
        {showTeam && <EventsCard className="self-start" />}
      </div>

      {showOrg && <OrgOverview />}
    </div>
  );
};

export default Dashboard;
